// The welcome checklist (decision 11; Accelerator.dc.html's "First visit" Tweak; AdminProducts.dc.html:
// "Members see this the first time they open {name}. It hides once they've ticked everything, or
// pressed Hide."). Pure, so a test can import it.
//
// - When it shows: the first time they open the product (the visit whose rows were read before it
//   was ever opened), and on later visits only while their first session is still to come, since
//   it's what to do before that session. A returning member whose sessions have started sees none,
//   as Accelerator.dc.html's "Returning member" and FEHome.dc.html's board day draw it.
// - The items are the product's checklist_items that come back for them: the database already
//   leaves out what isn't for them (The Funding Engine's 3 show only while their intake runs).
// - {next_session} in an item fills with their next session: "Sunday 25 October, 5pm", and {group}
//   with their group or intake ("Join the {group} WhatsApp." is "Join the Group 2 WhatsApp." for
//   Group 2 and "Join the Founding group 1 WhatsApp." for Founding group 1). An item whose
//   placeholder has nothing to fill isn't shown.
// - The line under the title counts what's left to do before their first session (their next, once
//   1 has run), or "to get started" when they have no session coming.
// - Each action does its thing and ticks the item: whatsapp opens the WhatsApp link (and shows only
//   when there is one: the intake's while it runs, or a mastermind's group's own, data.js), account
//   opens Account, calendar saves the sessions to their calendar, link opens its address.
// - An item that tells them to do Start here ("Do Start here: deal analysis, then marketing setup", POYT's
//   copied checklist) and has no button of its own gets Start, opening the product's Start here where
//   they're up to, as the calendar item has its button (member review, 11 Oct). The item has no action
//   in the database (action_kind none, as the content copy carried it to live), so it's known by its
//   words; startHereHref is given only for a product with Start here.
// - A mastermind's (mastermind: true; the Accelerator critique's member 12, 20 Oct, building the First visit
//   state Accelerator.dc.html drew), and no other type's:
//   - the calendar item that names {next_session} adds that 1 session (oneSession), not every session;
//   - the goal item ("Draft the goal you want to hit in 12 months...", known by its words as Start here's is) gets
//     Draft your goal, which takes them to the goal box, and is done once they have a goal (goalExists), saved now
//     or before, with no tick written;
//   - its twins on the home tick it too: Join the group's WhatsApp on Your group, and Add to calendar on the next
//     session, which turns this item's button to done as this 1 turns the card's to Added (calendarAdded, a flag
//     in the product's rows: ProductData.jsx).
import * as f from '../lib/format.js'

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

export function ordinal(n) {
  const k = n % 100
  if (k >= 11 && k <= 13) return `${n}th`
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`
}

export function nextSessionText(session) {
  if (!session?.starts_at) return null
  return `${f.longDate(session.starts_at)}, ${f.time(session.starts_at)}`
}

// { show, title, line, count, items: [{ id, text, done, action }] }
export const START_HERE_ITEM = /^Do Start here\b/i
// a mastermind's goal item, by its words (it has no action in the database)
export const GOAL_ITEM = /^Draft (the|your) goal\b/i

// The checklist item a home's twin ticks: by its action ('whatsapp', 'calendar', 'account'), or 'goal' by its words
export function itemByKind(items, kind) {
  const list = (items || []).slice().sort((a, b) => a.sort_order - b.sort_order)
  if (kind === 'goal') return list.find(i => (i.action_kind || 'none') === 'none' && GOAL_ITEM.test(i.body || '')) || null
  return list.find(i => i.action_kind === kind) || null
}

export function checklistModel({ product, items, ticks, state, sessions, whatsappUrl, nowMs, doneNow = false, firstVisit = !state?.last_opened_at, group = null, startHereHref = null, mastermind = false, goalExists = false }) {
  const list = (items || []).slice().sort((a, b) => a.sort_order - b.sort_order)
  const upcoming = (sessions || []).filter(s => s.starts_at && s.status !== 'cancelled'
    && Date.parse(s.starts_at) + (s.duration_minutes || 0) * 60e3 > nowMs)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))
  const next = upcoming[0] || null
  const live = (sessions || []).filter(s => s.starts_at && s.status !== 'cancelled')
  const started = live.some(s => Date.parse(s.starts_at) <= nowMs)
  const nextText = nextSessionText(next)

  const rows = list
    .filter(i => !(i.body.includes('{next_session}') && !nextText) && !(i.body.includes('{group}') && !group))
    .map(i => {
      const text = i.body.replace('{next_session}', nextText || '').split('{group}').join(group || '')
      let action = null
      if (i.action_kind === 'whatsapp' && whatsappUrl && /^https:\/\//.test(whatsappUrl)) action = { kind: 'whatsapp', label: i.action_label, url: whatsappUrl }
      if (i.action_kind === 'account') action = { kind: 'account', label: i.action_label, to: '/account' }
      if (i.action_kind === 'calendar' && upcoming.length) action = { kind: 'calendar', label: i.action_label, one: mastermind && i.body.includes('{next_session}') }
      if (i.action_kind === 'link' && i.action_target && /^(https:\/\/|\/[a-z0-9/_-]*$)/.test(i.action_target)) action = { kind: 'link', label: i.action_label, url: i.action_target }
      if (!action && (i.action_kind || 'none') === 'none' && startHereHref && START_HERE_ITEM.test(text)) action = { kind: 'link', label: 'Start', url: startHereHref }
      const goal = mastermind && !action && (i.action_kind || 'none') === 'none' && GOAL_ITEM.test(text)
      if (goal) action = { kind: 'goal', label: 'Draft your goal' }
      return { id: i.id, text, done: (ticks || []).includes(i.id) || (goal && goalExists), action }
    })

  const done = rows.filter(r => r.done).length
  const all = rows.length > 0 && done === rows.length
  const hidden = Boolean(state?.welcome_hidden_at)
  const what = next ? `to do before your ${started ? 'next' : 'first'} session` : 'to do to get started'
  return {
    // shown until it's hidden, or everything was ticked before this visit; on their first visit, or
    // while their first session is still to come
    show: rows.length > 0 && !hidden && (!all || doneNow) && (firstVisit || (live.length > 0 && !started)),
    title: `Welcome to ${product.name}`,
    line: `${plural(rows.length, 'thing', 'things')} ${what}.`,
    count: all ? (next ? `All done. See you on the ${ordinal(f.uk(next.starts_at).d)}.` : 'All done.') : `${done} of ${rows.length} done.`,
    items: rows,
    upcoming,
  }
}
